import React, { useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Clock,
  Globe,
  Briefcase,
  ArrowRight,
} from "lucide-react";
import {
  CASE_STUDIES,
  COMPANY_INFO,
  INDUSTRIES,
  INDUSTRY_ICONS,
} from "../data/mock";
import { toast } from "sonner";
import {
  EngagementModelsSection,
  FAQSection,
  TechnologiesSection,
  WhatWeDoSection,
} from "@/components/CommonSections";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import api from "../lib/api";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    company_name: "",
    email: "",
    phone_number: "",
    category: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Validate a single field; returns an error string ("" when valid).
  const validateField = (name, value) => {
    const v = (value || "").trim();
    switch (name) {
      case "full_name":
        if (!v) return "Full name is required.";
        if (v.length < 2) return "Name must be at least 2 characters.";
        if (v.length > 35) return "Name must be under 35 characters.";
        if (!/^[a-zA-Z\s.'-]+$/.test(v))
          return "Name can only contain letters.";
        return "";
      case "company_name":
        if (!v) return "Company name is required.";
        if (v.length < 2) return "Please enter a valid company name.";
        if (v.length > 40) return "Company name must be under 40 characters.";
        if (!/^[a-zA-Z0-9\s-]+$/.test(v))
          return "Only letters, numbers and hyphens are allowed.";
        return "";
      case "email":
        if (!v) return "Email address is required.";
        if (v.length > 100) return "Email must be under 100 characters.";
        if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(v))
          return "Enter a valid email address.";
        return "";
      case "phone_number": {
        if (!v) return "Phone number is required.";
        // Allow digits with an optional leading "+".
        if (!/^\+?\d*$/.test(v))
          return "Only numbers and a leading + are allowed.";
        if (v.length > 14) return "Phone must be at most 14 characters.";
        const digits = v.replace(/\D/g, "");
        if (digits.length < 7) return "Enter a valid phone number.";
        return "";
      }
      case "category":
        if (!v) return "Please select an option.";
        return "";
      case "description":
        if (!v) return "Please add some details.";
        if (v.length < 10) return "Please add at least 10 characters.";
        if (v.length > 1000)
          return "Please keep it under 1000 characters.";
        return "";
      default:
        return "";
    }
  };

  const validateAll = () => {
    const nextErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) nextErrors[key] = error;
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear/update the field error as the user types.
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) {
      toast.error(
        "We identified an issue with the information you provided. Please review the form again."
      );
      return;
    }
    setIsSubmitting(true);
    try {
      const { data } = await api.post("/contact/us", {
        full_name: formData.full_name,
        company_name: formData.company_name,
        category: formData.category,
        description: formData.description,
        email: formData.email,
        phone_number: formData.phone_number || null,
      });

      toast.success(
        data?.message ||
          "Thank you for your message! We'll get back to you within 24 hours."
      );
      setFormData({
        full_name: "",
        company_name: "",
        email: "",
        phone_number: "",
        category: "",
        description: "",
      });
      setErrors({});
    } catch (error) {
      const result = error.response?.data;
      if (result?.errors) {
        // Show the first validation message from the backend.
        Object.values(result.errors).forEach((messages) => {
          toast.error(messages[0]);
        });
      } else {
        toast.error(
          result?.message || "Unable to send message right now. Please try again."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    "AI Solutions",
    "Web Development",
    "Mobile App Development",
    "Custom Software Development",
    "IT Consulting",
    "Other",
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b32126] font-semibold">Get in Touch</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-gray-300">
              Ready to start your project? Have questions? We'd love to hear
              from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Contact Information
              </h2>

              <div className="space-y-6">
                {/* <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-[#b32126]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Phone</h3>
                    <a href={`tel:${COMPANY_INFO.phone}`} className="text-gray-600 hover:text-[#b32126] transition-colors">
                      {COMPANY_INFO.phone}
                    </a>
                  </div>
                </div> */}

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-[#b32126]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      Business Hours
                    </h3>
                    <span>Monday - Friday: 10AM - 7PM IST</span>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-[#b32126]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Address</h3>
                    <p className="text-gray-600">{COMPANY_INFO.address}</p>
                  </div>
                </div>
                <div className="w-full h-[300px] rounded-2xl overflow-hidden border border-white/10">
                  <iframe
                    title="Google Map"
                    src="https://www.google.com/maps?q=Infotech.Works%20Lane%205A%20Laxman%20Nagar%20Baner%20Pune&output=embed"
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>

                {/* <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="h-6 w-6 text-[#b32126]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      Global Delivery
                    </h3>
                    <p className="text-gray-600">Serving clients worldwide</p>
                  </div>
                </div> */}
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="full_name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={(e) => {
                          // Allow only letters, spaces and . ' - characters.
                          const cleaned = e.target.value.replace(
                            /[^a-zA-Z\s.'-]/g,
                            ""
                          );
                          setFormData((prev) => ({
                            ...prev,
                            full_name: cleaned,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            full_name: validateField("full_name", cleaned),
                          }));
                        }}
                        maxLength={35}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#b32126] focus:border-transparent transition-all ${
                          errors.full_name
                            ? "border-red-400"
                            : "border-gray-200"
                        }`}
                        placeholder="Enter your full name"
                      />
                      {errors.full_name && (
                        <p className="mt-1.5 text-sm text-red-500">
                          {errors.full_name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => {
                          // Allow only characters valid in an email address.
                          const cleaned = e.target.value.replace(
                            /[^A-Za-z0-9._%+@-]/g,
                            ""
                          );
                          setFormData((prev) => ({
                            ...prev,
                            email: cleaned,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            email: validateField("email", cleaned),
                          }));
                        }}
                        maxLength={100}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#b32126] focus:border-transparent transition-all ${
                          errors.email ? "border-red-400" : "border-gray-200"
                        }`}
                        placeholder="Enter your email address"
                      />
                      {errors.email && (
                        <p className="mt-1.5 text-sm text-red-500">
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="company_name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={(e) => {
                          // Allow only letters, numbers, spaces and hyphens.
                          const cleaned = e.target.value.replace(
                            /[^a-zA-Z0-9\s-]/g,
                            ""
                          );
                          setFormData((prev) => ({
                            ...prev,
                            company_name: cleaned,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            company_name: validateField("company_name", cleaned),
                          }));
                        }}
                        maxLength={40}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#b32126] focus:border-transparent transition-all ${
                          errors.company_name
                            ? "border-red-400"
                            : "border-gray-200"
                        }`}
                        placeholder="Enter your company name"
                      />
                      {errors.company_name && (
                        <p className="mt-1.5 text-sm text-red-500">
                          {errors.company_name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="phone_number"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone_number"
                        name="phone_number"
                        inputMode="numeric"
                        value={formData.phone_number}
                        onChange={(e) => {
                          // Allow only digits and a single leading "+".
                          let cleaned = e.target.value.replace(/[^\d+]/g, "");
                          const hasPlus = cleaned.startsWith("+");
                          cleaned =
                            (hasPlus ? "+" : "") + cleaned.replace(/\+/g, "");
                          // Cap total length at 14 characters (including +).
                          cleaned = cleaned.slice(0, 14);
                          setFormData((prev) => ({
                            ...prev,
                            phone_number: cleaned,
                          }));
                          // Clear any error while typing; validate on blur.
                          setErrors((prev) => ({ ...prev, phone_number: "" }));
                        }}
                        onBlur={(e) =>
                          setErrors((prev) => ({
                            ...prev,
                            phone_number: validateField(
                              "phone_number",
                              e.target.value
                            ),
                          }))
                        }
                        maxLength={14}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#b32126] focus:border-transparent transition-all ${
                          errors.phone_number
                            ? "border-red-400"
                            : "border-gray-200"
                        }`}
                        placeholder="Enter your phone number"
                      />
                      {errors.phone_number && (
                        <p className="mt-1.5 text-sm text-red-500">
                          {errors.phone_number}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="category"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Select an option *
                    </label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => {
                        setFormData((prev) => ({ ...prev, category: value }));
                        setErrors((prev) => ({
                          ...prev,
                          category: validateField("category", value),
                        }));
                      }}
                    >
                      <SelectTrigger
                        id="category"
                        className={`w-full h-auto px-4 py-3 border rounded-lg text-gray-900 focus:ring-2 focus:ring-[#b32126] focus:border-transparent transition-all ${
                          errors.category ? "border-red-400" : "border-gray-200"
                        }`}
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service} value={service}>
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.category && (
                      <p className="mt-1.5 text-sm text-red-500">
                        {errors.category}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      More Details *
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      maxLength={1000}
                      rows={5}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#b32126] focus:border-transparent transition-all resize-none ${
                        errors.description ? "border-red-400" : "border-gray-200"
                      }`}
                      placeholder={`Tell us about your project, goals, and timeline...`}
                    />
                    {errors.description && (
                      <p className="mt-1.5 text-sm text-red-500">
                        {errors.description}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full md:w-auto inline-flex items-center justify-center px-8 py-4 bg-[#b32126] text-white font-semibold rounded-lg hover:bg-[#8f1a1f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WhatWeDoSection />

      {/* Industries Section - Show first 2 rows (12 items on desktop) */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Deep expertise across {INDUSTRIES.length}+ industries, delivering
              specialized solutions
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {INDUSTRIES.slice(0, 12).map((industry) => {
              const IconComponent = INDUSTRY_ICONS[industry.slug] || Building2;
              return (
                <Link
                  key={industry.slug}
                  to={`/industries/${industry.slug}`}
                  className="group p-4 bg-gray-800 rounded-xl text-center hover:bg-[#b32126] transition-all"
                >
                  <div className="w-10 h-10 mx-auto mb-3 bg-gray-700 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <IconComponent className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-gray-300 group-hover:text-white text-sm font-medium block">
                    {industry.title}
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/industries"
              className="inline-flex items-center text-[#b32126] hover:text-[#d42a30] font-semibold"
            >
              View All Industries
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real clients across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CASE_STUDIES.slice(0, 3).map((study) => (
              <Link
                key={study.id}
                to={`/resources/case-studies/${study.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#b32126] text-white text-xs font-medium rounded-full">
                      {study.industry}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/90 text-gray-700 text-xs font-medium rounded-full">
                      {study.timeline}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-1">
                    {study.clientType}
                  </p>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {study.tagline}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.results.slice(0, 3).map((result, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        {result.value} {result.label}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/resources/case-studies"
              className="inline-flex items-center px-6 py-3 border-2 border-[#b32126] text-[#b32126] font-semibold rounded-lg hover:bg-[#b32126] hover:text-white transition-colors"
            >
              View All Case Studies
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <TechnologiesSection />
      <EngagementModelsSection bgColor="bg-white" />
      <FAQSection />
    </div>
  );
};

export default ContactPage;
